import { Component, ElementRef, HostListener, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { TermsUtils } from '../../../modules/map/utils/terms.utils';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, Subject } from 'rxjs';
import { AlphabetPipe } from '../../pipes/alphabet.pipe';
import { CommunicationService } from '../../../core/services/communication.service';
import { takeUntil } from 'rxjs/internal/operators';

@Component({
  selector: 'search',
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.scss']
})
export class SearchComponent implements OnInit, OnChanges, OnDestroy {

  @Input() sideNavigation: any[];
  @Input() placeholder: string = '';
  resources: any[] = [];
  searchResultList: any[] = [];
  searchQuery: string = '';
  maxResultListLength = 30;
  configuration: any;
  showResultList: boolean = false;

  portalConfigurationSubscription$ = new Subject();

  constructor(private router: Router,
    private route: ActivatedRoute,
    private communicationService: CommunicationService,
    private el: ElementRef) {
  }

  ngOnInit() {
    combineLatest(this.route.params, this.route.queryParams).subscribe(value => {
      this.searchQuery = '';
      this.searchResultList = [];
    });

    this.communicationService.portalConfigurationChange$
      .pipe(takeUntil(this.portalConfigurationSubscription$))
      .subscribe(configuration => {
        this.configuration = configuration;
        this.getResources(this.sideNavigation);
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if(this.configuration){
      this.getResources(this.sideNavigation);
      this.search(this.searchQuery);
    }
  }

  getResources(sideNavigation: any) {
    let resources = [];
    sideNavigation.forEach(element => {
      this.generateResources(element, {}, resources);
    });
    this.resources = resources;
  }

  generateResources(element: any, sections: any, resources: any[]) {
    if (element.hasOwnProperty('subsections')) {
      sections[element.type] = { name: element.name, label: element.label };
      element.subsections.forEach(e => this.generateResources(e, sections, resources));
    }
    else {
      if (this.configuration.searchConfiguration.searchItemTypes.includes(element.type)) {
        let resource = this.createResource(element, sections);
        resources.push(resource);
      }
    }
  }

  createResource(element: any, sections: any[]): any {
    let resource = {
      name: element.name,
      label: element.label,
      id: element.id,
      locationId: element.locationId,
      value: element.value,
      sections: {}
    };
    Object.keys(sections).forEach(key => {
      resource.sections[key] = sections[key];
    });
    resource.sections[element.type] = { name: resource.name, label: resource.label };
    return resource;
  }

  search(query) {
    query = new AlphabetPipe().transform(query, "cyrillic-mk");
    this.searchQuery = query;
    query = query.toLowerCase();
    if (this.searchQuery === '') {
      this.searchResultList = [];
      return;
    }
    this.searchResultList = this.resources.filter(resource => {
      let containsInSections = Object.keys(resource.sections)
        .some(section => resource.sections[section].name.toLowerCase().includes(query));
      return resource.name.toLowerCase().includes(query) || containsInSections;
    }).map(resource => {
      resource['order'] = resource.name.toLowerCase().indexOf(query);
      return resource;
    }).sort((r1, r2) => {
      if (r1['order'] === -1) {
        return 1;
      }
      return r1['order'] - r2['order'];
    });
    if (this.searchResultList.length > this.maxResultListLength) {
      this.searchResultList = this.searchResultList.splice(0, this.maxResultListLength);
    }
  }

  onResourceClick(resource) {
    let pathParam = resource.sections[''].label;
    let queryParams = {};
    this.searchQuery = resource.name;
    Object.keys(resource.sections).filter(key => key !== '')
      .map(key => queryParams[key] = resource.sections[key].label);
    this.showResultList = false;
    this.searchResultList = [];
    this.router.navigate(['/map', pathParam], { queryParams: queryParams });
  }

  getSections(element: any): string [] {
    return Object.keys(element.sections).filter(
      section => this.configuration.searchConfiguration.additionalInformationTypes.includes(section));
  }

  displayTermsAvailabilities(element): boolean {
    const { displayTermsAvailability } = this.configuration.searchConfiguration;
    return displayTermsAvailability && element.value !== null;
  }

  getTermClass(value): string {
    return TermsUtils.getTermClass(value);
  }

  getIcon(section) {
    const { searchIcons } = this.configuration;
    return searchIcons[section];
  }

  hasIcon(section) {
    return this.configuration.searchIcons[section] !== undefined;
  }

  emptySearchResults() {
    this.searchQuery = '';
    this.searchResultList = [];
  }

  hasResults(): boolean {
    return this.showResultList && (this.searchResultList.length > 0 || this.searchQuery.length > 0);
  }

  @HostListener('document: click', ['$event'])
  onDocumentClick(event: any) {
    if (!this.el.nativeElement.contains(event.target)) {
      this.showResultList = false;
    }
  }

  onFocus() {
    this.showResultList = true;
  }

  ngOnDestroy() {
    this.portalConfigurationSubscription$.next();
    this.portalConfigurationSubscription$.complete();
  }

}
